import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useParams } from "react-router-dom";
import Sidebar from "../../../DesignSystem/Sidebar";
import Breadcrumb from "../../../DesignSystem/Breadcrumb/Breadcrumb";
import DateField from "../../../DesignSystem/Inputs/DateField";
import InputField from "../../../DesignSystem/Inputs/InputField";
import CustomSelect from "../../../DesignSystem/Dropdowns/CustomSelect";
import CheckboxGroup from "../../../DesignSystem/Inputs/CheckboxGroup";
import RadioGroup from "../../../DesignSystem/Inputs/RadioGroup";
import FileUpload from "../../../DesignSystem/Upload/FileUpload";
import UploadedFileList from "../../../DesignSystem/Upload/UploadedFileList";
import PrimaryButton from "../../../DesignSystem/Buttons/PrimaryButton";
import SecondaryButton from "../../../DesignSystem/Buttons/SecondaryButton";
import Banner from "../../../DesignSystem/Banner/Banner";
import WebsiteAuditLogs from "../components/WebsiteAuditLogs";
import { useUserPermissions } from "../../../SignIn/UserPermissionsContext";
import { useAdviserListWithUnappointed } from "../../../../context/Adviser/Utils/UnappointedAppointedAdviserEmailList";
import { auth } from "../../../../backend/firebaseConfig";
import { APP_ROUTES } from "../../../../constants/appRoutes";
import { calculateAmountToCharge } from "../websiteConstants";
import { createPrintingCharge, subscribePrintingAuditLogs, subscribePrintingCharge, subscribePrintingOptions, updatePrintingCharge } from "../services/printingChargeService";
import "../../../../css/Marketing/FinancialPromotions/FinancialPromotions.css";
import "../../../../css/Marketing/FinancialPromotions/FinancialPromotionsSubmission.css";
import "../../../../css/Marketing/WebsiteCharges.css";

const now = new Date();
const today = [now.getFullYear(), String(now.getMonth() + 1).padStart(2, "0"), String(now.getDate()).padStart(2, "0")].join("-");

const PrintingChargeEntryPage = () => {
  const navigate = useNavigate();
  const { printingId } = useParams();
  const editing = Boolean(printingId);
  const { userPermissions } = useUserPermissions();
  const canViewAuditLogs = Array.isArray(userPermissions) && userPermissions.some((permission) => String(permission || "").trim().toLowerCase().includes("administrator"));
  const { advisers, loadingAdvisers } = useAdviserListWithUnappointed();
  const [formData, setFormData] = useState({
    orderDate: today,
    adviserName: "",
    adviserEmail: "",
    supplier: "",
    orderItems: [],
    orderNumber: "",
    orderDescription: "",
    totalPaid: "",
    markup: "20",
    amountToCharge: "",
    chargeAcross: "",
  });
  const [invoice, setInvoice] = useState(null);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [printingOptions, setPrintingOptions] = useState({ orderItems: [], suppliers: [] });
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [entryLoading, setEntryLoading] = useState(editing);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(editing && canViewAuditLogs);

  useEffect(
    () => subscribePrintingOptions(
      (options) => {
        setPrintingOptions(options);
        setOptionsLoading(false);
      },
      () => {
        setSaveError("We couldn't load the printing suppliers and order items.");
        setOptionsLoading(false);
      },
    ),
    [],
  );

  useEffect(() => {
    if (!printingId) return undefined;
    return subscribePrintingCharge(
      printingId,
      (entry) => {
        if (!entry) {
          navigate(`${APP_ROUTES.MARKETING_WEBSITES}?tab=printing`, { replace: true, state: { activeTab: "printing" } });
          return;
        }
        setFormData((current) => ({ ...current, ...entry, orderItems: Array.isArray(entry.orderItems) ? entry.orderItems : [] }));
        setInvoice(entry.invoice || null);
        setEntryLoading(false);
      },
      () => {
        setSaveError("We couldn't load this printing charge.");
        setEntryLoading(false);
      },
    );
  }, [printingId, navigate]);

  useEffect(() => {
    if (!printingId || !canViewAuditLogs) { setAuditLoading(false); return undefined; }
    return subscribePrintingAuditLogs(printingId, (logs) => { setAuditLogs(logs); setAuditLoading(false); }, () => setAuditLoading(false));
  }, [printingId, canViewAuditLogs]);

  const adviserOptions = useMemo(
    () =>
      advisers.map((adviser) => {
        const name =
          [adviser.firstName, adviser.lastName]
            .filter(Boolean)
            .join(" ")
            .trim() ||
          adviser.name ||
          "";
        return {
          id: adviser.email,
          name: `${adviser.email}${name ? ` (${name})` : ""}`,
        };
      }),
    [advisers],
  );

  const fieldIsValid = (field, value) => {
    if (["orderDate", "adviserName", "supplier"].includes(field)) {
      return Boolean(String(value || "").trim());
    }
    if (field === "orderItems") return Array.isArray(value) && value.length > 0;
    if (field === "totalPaid") return /^-?\d+(\.\d{1,2})?$/.test(String(value || ""));
    if (field === "markup") return /^-?\d+(\.\d+)?$/.test(String(value || ""));
    if (field === "chargeAcross") return !value || /^\d+$/.test(String(value));
    return true;
  };

  const updateFieldError = (field, value) => {
    setErrors((current) => {
      const next = { ...current };
      if (fieldIsValid(field, value)) delete next[field];
      else next[field] = true;
      return next;
    });
  };

  const updateAdviser = (event) => {
    const adviserEmail = event.target.value;
    const adviser = advisers.find(
      (item) =>
        String(item.email || "")
          .trim()
          .toLowerCase() === String(adviserEmail).trim().toLowerCase(),
    );
    const adviserName =
      [adviser?.firstName, adviser?.lastName]
        .filter(Boolean)
        .join(" ")
        .trim() ||
      adviser?.name ||
      "";
    setFormData((current) => ({ ...current, adviserEmail, adviserName }));
    updateFieldError("adviserName", adviserName);
  };

  const updateField = (field) => (event) => {
    const value = event.target.value;
    setFormData((current) => ({ ...current, [field]: value }));
    updateFieldError(field, value);
  };

  const updateOrderItems = (orderItems) => {
    setFormData((current) => ({ ...current, orderItems }));
    updateFieldError("orderItems", orderItems);
  };

  const updateMoney = (field) => (event) => {
    const value = event.target.value;
    setFormData((current) => {
      const next = { ...current, [field]: value };
      next.amountToCharge = calculateAmountToCharge(next.totalPaid, next.markup);
      return next;
    });
    updateFieldError(field, value);
  };

  const validate = () => {
    const next = {};
    if (!formData.orderDate) next.orderDate = true;
    if (!formData.adviserName) next.adviserName = true;
    if (!formData.supplier) next.supplier = true;
    if (!formData.orderItems.length) next.orderItems = true;
    if (!formData.totalPaid || !/^-?\d+(\.\d{1,2})?$/.test(formData.totalPaid)) next.totalPaid = true;
    if (!/^-?\d+(\.\d+)?$/.test(formData.markup)) next.markup = true;
    if (formData.chargeAcross && !/^\d+$/.test(formData.chargeAcross)) next.chargeAcross = true;
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;
    setSaveError("");
    setSaving(true);
    try {
      const actor = { uid: auth.currentUser?.uid || "", email: auth.currentUser?.email || localStorage.getItem("userEmail") || "", displayName: auth.currentUser?.displayName || "" };
      if (editing) await updatePrintingCharge({ printingId, formData, invoice, actor });
      else await createPrintingCharge({ formData, invoice, actor });
      navigate(`${APP_ROUTES.MARKETING_WEBSITES}?tab=printing`, { replace: true, state: { activeTab: "printing", successMessage: editing ? "Printing charge updated successfully." : "Printing charge saved successfully." } });
    } catch (error) {
      setSaveError(error.message || "We couldn't save this printing charge.");
    } finally {
      setSaving(false);
    }
  };

  if (entryLoading) {
    return <div className="Main-Page"><Sidebar /><main className="Main-Container website-loading">Loading printing charge…</main></div>;
  }

  return (
    <div className="Main-Page">
      <Helmet>
        <title>
          {editing ? "Printing Charge Details" : "Add New Printing Charge"}
        </title>
      </Helmet>
      <Sidebar />
      <main className="Main-Container fp-submission-container">
        <Breadcrumb
          items={[
            { label: "Marketing", to: APP_ROUTES.DEPARTMENT_MARKETING },
            {
              label: "design⁴",
              to: `${APP_ROUTES.MARKETING_WEBSITES}?tab=printing`,
            },
            { label: editing ? "Printing Charge Details" : "Add New Entry" },
          ]}
        />
        <div className="FP_H3">
          {editing ? "Printing Charge Details" : "Add New Printing Charge"}
        </div>
        {saveError && <Banner type="error" message={saveError} />}
        <form onSubmit={handleSubmit}>
          <section className="fp-form-container fit-column gap-l">
            <div className="website-fields">
              <DateField
                label="Order Date"
                required
                value={formData.orderDate}
                error={errors.orderDate}
                onChange={updateField("orderDate")}
              />
              <div aria-hidden="true" />
              <CustomSelect
                label="Adviser Email"
                options={adviserOptions}
                value={formData.adviserEmail}
                placeholder={
                  loadingAdvisers ? "Loading advisers…" : "Select Adviser Email"
                }
                disabled={loadingAdvisers}
                onChange={updateAdviser}
                clearable
              />
              <InputField
                label="Adviser Name"
                required
                value={formData.adviserName}
                placeholder="Adviser Name"
                error={errors.adviserName}
                onChange={updateField("adviserName")}
              />
            </div>
            <div className="fit-column gap-s">
              <RadioGroup
                name="supplier"
                label="Supplier"
                required
                options={printingOptions.suppliers.map((value) => ({
                  value,
                  label: value,
                }))}
                value={formData.supplier}
                error={errors.supplier}
                onChange={updateField("supplier")}
                inline
                disabled={optionsLoading}
              />
            </div>
            <div className="fit-column gap-s">
              <div className="ds-input-label FP_Content text-black">
                Order Items
                <span className="FP_Content_Semibold text-error">*</span>
              </div>
              <CheckboxGroup
                formDataKey="orderItems"
                options={printingOptions.orderItems}
                selectedOptions={formData.orderItems}
                error={errors.orderItems}
                onChange={updateOrderItems}
                columns={4}
                disabled={optionsLoading}
              />
            </div>
            <div className="website-fields">
              <InputField
                label="Order Number"
                value={formData.orderNumber}
                placeholder="Order number"
                onChange={updateField("orderNumber")}
              />
              <InputField
                label="Order Description"
                value={formData.orderDescription}
                placeholder="Details of the ordered item"
                onChange={updateField("orderDescription")}
              />
              <InputField
                label="Total Paid (£)"
                required
                type="number"
                step="0.01"
                value={formData.totalPaid}
                error={errors.totalPaid}
                placeholder="Total amount paid to supplier"
                onChange={updateMoney("totalPaid")}
              />
              <InputField
                label="Markup (%)"
                tooltip="A default markup of 20% is added to the supplier cost."
                required
                type="number"
                step="0.01"
                value={formData.markup}
                error={errors.markup}
                onChange={updateMoney("markup")}
              />
              <InputField
                label="Amount to Charge (£)"
                required
                type="number"
                value={formData.amountToCharge}
                readOnly
              />
              <InputField
                label="Charge Across (No of Months)"
                type="number"
                min="1"
                step="1"
                value={formData.chargeAcross}
                error={errors.chargeAcross}
                onChange={updateField("chargeAcross")}
              />
            </div>
            <div className="website-upload-block">
              <FileUpload
                label="Invoice"
                accept=".pdf,.png,.jpg,.jpeg"
                fileName={invoice?.name}
                onFilesChange={setInvoice}
              />
              {invoice && (
                <UploadedFileList
                  files={[invoice]}
                  onRemove={() => setInvoice(null)}
                />
              )}
            </div>
          </section>
          <div className="printing-charge-actions row justify-space-between">
            <SecondaryButton
              type="button"
              label="Cancel"
              onClick={() =>
                navigate(`${APP_ROUTES.MARKETING_WEBSITES}?tab=printing`, {
                  state: { activeTab: "printing" },
                })
              }
            />
            <PrimaryButton
              label={
                saving ? "Saving…" : editing ? "Save Changes" : "Add Entry"
              }
              disabled={saving}
            />
          </div>
          {editing && canViewAuditLogs && (
            <WebsiteAuditLogs logs={auditLogs} loading={auditLoading} />
          )}
        </form>
      </main>
    </div>
  );
};

export default PrintingChargeEntryPage;
